<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>Tag Management</h1>
      </div>
    </div>

    <div class="container-fluid">

      <div class="dashboard-block">

        <div class="w-full flex flex-wrap justify-between items-center">
          <ul class="nav nav-tabs" role="tablist">
            <li :class="{ 'active': filters.entity === 'career' }">
              <router-link :to="{ query: { ...$route.query, entity: 'career', type: 'sport', page: 1 } }">Career</router-link>
            </li>
            <li :class="{ 'active': filters.entity === 'interest-categories' }">
              <router-link :to="{ query: { ...$route.query, entity: 'interest-categories', page: 1 } }">Interests</router-link>
            </li>
            <li :class="{ 'active': filters.entity === 'personal-interests' }">
              <router-link :to="{ query: { ...$route.query, entity: 'personal-interests', page: 1 } }">Hobbies</router-link>
            </li>
            <li v-if="!$vlastMode" :class="{ 'active': filters.entity === 'description' }">
              <router-link :to="{ query: { ...$route.query, entity: 'description', type: 'description', page: 1 } }">Description</router-link>
            </li>
            <li :class="{ 'active': filters.entity === 'sponsors' }">
              <router-link :to="{ query: { ...$route.query, entity: 'sponsors', type: 'sponsor', page: 1 } }">Sponsors</router-link>
            </li>
          </ul>
        </div>

        <div class="tags-panel">
          <div class="tags-panel__container">
            <div v-if="filters.entity === 'career'" class="tags-panel__column margin-r-1">
              <div class="tags-panel__toolbar flex flex-wrap w-full">
                <label class="control-label">Filter by tag group:</label>
                <button @click="resetTagType" class="tags-panel__clear flex-shrink-0">Reset selection</button>
              </div>
              <selectize
                class="selectize selectize-single form-control"
                name="tag_type"
                :options="tagTypeOptions"
                v-model="filters.type"
                :disable-delete="true"
                @input="handleGetEntities">
              </selectize>
            </div>
            <div class="tags-panel__column" :class="{ 'margin-l-1': filters.entity === 'career' }">
              <form @submit.prevent>
                <div class="tags-panel__toolbar flex flex-wrap w-full">
                  <label class="control-label">Find a tag</label>
                  <button type="button" @click="resetSearch" class="tags-panel__clear">Reset search</button>
                </div>
                <div class="input-group">
                  <input v-model="filters.term" type="text" class="form-control" placeholder="Search tags...">
                  <span class="input-group-btn">
                    <ladda-button :click="handleGetEntities" class="btn" type="submit"><i class="fa fa-search"></i></ladda-button>
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div v-if="filters.status === 'active'" class="tags-panel">
          <system-tag-management-form
            :selected-tags="selectedTags"
            :tag-type-options="tagTypeOptions"
            :loading="performingAction"
            v-model="selectedTags"
            @create="handleCreateEntity"
            @rename="handleRenameEntity"
            @merge="handleMergeEntities"
            @mergeInto="handleMergeIntoEntities"
          >
            <button @click="resetSelectedTags" class="tags-panel__clear m-l-md flex-shrink-0" :disabled="selectedTags.length < 1">Clear selection</button>
          </system-tag-management-form>
        </div>

        <div class="tags-panel__table">

          <div class="w-full flex flex-wrap justify-between items-center">
            <ul class="nav nav-tabs" role="tablist">
              <li :class="{ 'active': filters.status === 'active' }">
                <router-link :to="{ query: { ...$route.query, status: 'active' } }">Active</router-link>
              </li>
              <li :class="{ 'active': filters.status === 'pending' }">
                <router-link :to="{ query: { ...$route.query, status: 'pending' } }">Pending</router-link>
              </li>
            </ul>
          </div>

          <table class="table table-hover table-tags table-data">
            <thead>
              <tr>
                <th class="check"></th>
                <th class="name">Tag Name</th>
                <th class="occurrences">Occurrences</th>
                <th class="delete">Delete</th>
                <th v-if="hasEditPage" class="manage">Manage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="fetchingTags">
                <td colspan="4" class="text-center">
                  <i class="fa fa-circle-o-notch fa-spin fa-2x fa-fw text-muted"></i>
                  <span class="sr-only">Loading...</span>
                </td>
              </tr>
              <tr v-else-if="haveNoEntities">
                <td colspan="4" class="text-center">
                  There are no tags. Check the tag type or search term.
                </td>
              </tr>
              <system-tag-management-list-item
                v-else
                v-for="tag in currentEntityList"
                :key="tag.id"
                :tag="tag"
                :has-edit="hasEditPage"
                v-model="selectedTags"
                @delete="handleDeleteEntity"/>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end" v-if="filters.status === 'pending' && selectedTags.length > 0">
          <div style="width: 200px;" class="margin-r-1">
            <selectize
              v-if="filters.entity === 'interest-categories' || 'career'"
              class="selectize selectize-single form-control"
              name="tag_type"
              :options="tagTypeOptions"
              v-model="interestCategoryApproveGroup"
              :disable-delete="true">
            </selectize>
          </div>
          <button
            class="approve-tags-btn btn btn-success"
            @click="handleApproveEntities"
          >
            Approve Selected ({{ selectedTags.length }})
          </button>
        </div>

        <div class="flex flex-wrap w-full">
          <system-tag-management-pagination class="w-full" :pagination="currentEntityPagination" @changePage="handleUpdatePagination"/>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { assign, each } from 'lodash'
import SystemTagManagementPagination from './SystemTagManagementPagination'
import SystemTagManagementListItem from './SystemTagManagementListItem'
import SystemTagManagementForm from './SystemTagManagementForm'
import Selectize from '../ext/codium/CodiumSelectize'
import { alert, http, notify } from 'utils'

export default {
  components: {
    SystemTagManagementPagination,
    SystemTagManagementListItem,
    SystemTagManagementForm,
    Selectize
  },

  data () {
    return {
      fetchingTags: false,
      selectedTags: [],
      filters: {
        page: 1,
        term: null,
        type: 'sport',
        entity: 'career',
        status: 'active'
      },
      interestCategoryApproveGroup: 'industry',
      performingAction: false,
      interestCategoryGroups: [],
      interestCategories: [],
      interestCategoriesPagination: {},
      personalInterests: [],
      personalInterestsPagination: {}
    }
  },

  watch: {
    /**
     * When the page changes fetch the tags.
     *
     * @return void
     */
    currentPage () {
      this.filters.page = this.currentPage
      this.handleGetEntities()
    },

    'filters.type' () {
      this.selectedTags = []
      this.handleGetEntities()
    },

    'filters.entity' () {
      this.selectedTags = []
      this.handleGetEntities()
    },

    '$route.query.type' (newValue) {
      this.filters.type = newValue
    },

    '$route.query.entity' (newValue) {
      this.filters.entity = newValue
    },

    '$route.query.status' (newValue) {
      this.filters.status = newValue
      this.handleGetEntities()
    },

    '$route.query.page' (newValue) {
      this.handleUpdatePagination(newValue)
    }
  },

  computed: {
    ...mapState('tag', ['tags', 'pagination']),

    hasEditPage () {
      return true
    },

    /**
     * Determine if we have no tags.
     *
     * @return bool
     */
    haveNoEntities () {
      return !this.currentEntityList || this.currentEntityList.length === 0
    },

    tagTypeOptions () {
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.interestCategoryGroups ? this.interestCategoryGroups.map(element => { return { id: element, text: element } }) : []
        case 'personal-interests':
          return []
        case 'career':
          const options = [
            { id: 'sport', text: this.$isNrlClient ? 'Category' : this.$isWSL ? 'Nation' : 'Sport', show: true },
            { id: 'league', text: this.$t('League'), show: true },
            { id: 'club', text: this.$t('Club'), show: true },
            { id: 'charity', text: 'Charity', show: true },
            { id: 'theme', text: this.$t('Theme'), show: true },
            { id: 'industry', text: 'Industry', show: true },
            { id: 'position', text: this.$isWSL ? 'Languages' : this.$t('Position'), show: this.$positionTags },
            { id: 'award', text: this.$t('Awards'), show: this.$awardTags },
            { id: 'highSchool', text: 'High School', show: this.$highSchoolTags },
            { id: 'college', text: 'College', show: this.$collegeTags },
            { id: 'degreeCompleted', text: 'Degree Completed', show: this.$degreeCompletedTags },
            { id: 'budgetRange', text: 'Budget Range', show: this.$budgetRangeTags },
            { id: 'proBowl', text: 'Pro Bowl', show: this.$proBowlTags },
            { id: 'charity', text: 'Charity', show: this.$charityTags },
            { id: 'firstContract', text: 'Year of first contract signed', show: this.$firstContractTags }
          ]
          return options.filter(element => element.show)
        default:
          return []
      }
    },

    /**
     * Alias for getting the current page.
     *
     * @return int
     */
    currentPage () {
      return this.currentEntityPagination.current_page
    },

    /**
     * Get the query params for the billboard booking request.
     *
     * @return object
     */
    queryParams () {
      let params = {}

      each(this.filters, (value, param) => {
        if (value) {
          params[param] = value
        }
      })
      params['aliases'] = false

      return params
    },

    currentEntityList () {
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.interestCategories
        case 'personal-interests':
          return this.personalInterests
        default:
          return this.tags
      }
    },

    currentEntityPagination () {
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.interestCategoriesPagination
        case 'personal-interests':
          return this.personalInterestsPagination
        default:
          return this.pagination
      }
    }
  },

  created () {
    this.prefillFilters()

    this.handleGetEntities()

    this.getInterestCategoryGroups()
  },

  methods: {
    ...mapActions('tag', ['getTags', 'resetTags', 'approveTags', 'storeTag', 'renameTag', 'mergeTags', 'mergeIntoTags', 'deleteTag', 'setTagsPaginationProperty']),

    async getInterestCategories (params) {
      const response = await http.get('interest-categories', { params })
      this.interestCategories = response.data.data
      this.interestCategoriesPagination = response.data.meta
      this.fetchingTags = false
      return response.data
    },

    async updateInterestCategory ({ id, payload }) {
      this.performingAction = true
      const response = await http.post(`interest-categories/${id}/update`, payload)
      const index = this.interestCategories.findIndex(element => element.id === id)
      if (index !== -1) {
        this.interestCategories.splice(index, 1, response.data)
      }
      this.performingAction = false
      return response.data
    },

    async mergeInterestCategories (payload) {
      this.performingAction = true
      const response = await http.post('interest-categories/merge', payload)
      this.interestCategories.splice(0, 0, response.data)
      payload.ids.forEach(id => {
        const index = this.interestCategories.findIndex(element => element.id === id)
        if (index !== -1) {
          this.interestCategories.splice(index, 1)
        }
      })
      this.performingAction = false
      return response.data
    },

    async mergePersonalInterests (payload) {
      this.performingAction = true
      const response = await http.post('personal-interests/merge', payload)
      this.personalInterests.splice(0, 0, response.data)
      payload.ids.forEach(id => {
        const index = this.personalInterests.findIndex(element => element.id === id)
        if (index !== -1) {
          this.personalInterests.splice(index, 1)
        }
      })
      this.performingAction = false
      return response.data
    },

    async getInterestCategoryGroups () {
      const response = await http.get('interest-category-groups')
      this.interestCategoryGroups = response.data
      return response.data
    },

    async getPersonalInterests (params) {
      const response = await http.get('personal-interests', { params })
      this.personalInterests = response.data.data
      this.personalInterestsPagination = response.data.meta
      this.fetchingTags = false
      return response.data
    },

    async updatePersonalInterest ({ id, payload }) {
      this.performingAction = true
      const response = await http.post(`personal-interests/${id}/update`, payload)
      const index = this.personalInterests.findIndex(element => element.id === id)
      if (index !== -1) {
        this.personalInterests.splice(index, 1, response.data)
      }
      this.performingAction = false
      return response.data
    },

    async approvePersonalInterests (ids) {
      this.performingAction = true
      const response = await http.post(`personal-interests/update`, { ids, pending: false })
      ids.forEach(id => {
        const index = this.personalInterests.findIndex(element => element.id === id)
        if (index !== -1) {
          this.personalInterests.splice(index, 1)
        }
      })
      this.performingAction = false
      return response
    },

    async approveInterestCategories (ids) {
      if (!this.interestCategoryApproveGroup) {
        notify.error('A group must be selected to approve interests')
        return Promise.resolve()
      }
      this.performingAction = true
      const response = await http.post(`interest-categories/update`, { ids, pending: false, group: this.interestCategoryApproveGroup })
      ids.forEach(id => {
        const index = this.interestCategories.findIndex(element => element.id === id)
        if (index !== -1) {
          this.interestCategories.splice(index, 1)
        }
      })
      this.performingAction = false
      return response
    },

    async createPersonalInterest (payload) {
      this.performingAction = true
      const response = await http.post('personal-interests/create', payload)
      this.personalInterests.splice(0, 0, response.data)
      this.performingAction = false
      return response.data
    },

    async createInterestCategory (payload) {
      this.performingAction = true
      const response = await http.post('interest-categories/create', payload)
      this.interestCategories.splice(0, 0, response.data)
      this.performingAction = false
      return response.data
    },

    async deletePersonalInterest (id) {
      const response = await http.delete(`personal-interests/${id}/delete`)
      const index = this.personalInterests.findIndex(element => element.id === id)
      if (index !== -1) {
        this.personalInterests.splice(index, 1)
      }
      return response.data
    },

    async deleteInterestCategory (id) {
      const response = await http.delete(`interest-categories/${id}/delete`)
      const index = this.interestCategories.findIndex(element => element.id === id)
      if (index !== -1) {
        this.interestCategories.splice(index, 1)
      }
      return response.data
    },

    handleUpdatePagination (value) {
      switch (this.filters.entity) {
        case 'interest-categories':
          this.interestCategoriesPagination.current_page = value
          return Promise.resolve()
        case 'personal-interests':
          this.personalInterestsPagination.current_page = value
          return Promise.resolve()
        default:
          return this.setTagsPaginationProperty({property: 'current_page', value})
      }
    },

    handleApproveEntities () {
      const ids = this.selectedTags.map((tag) => {
        return tag.id
      })
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.approveInterestCategories(ids).then(() => {
            this.resetSelectedTags()
          })
        case 'personal-interests':
          return this.approvePersonalInterests(ids).then(() => {
            this.resetSelectedTags()
          })
        case 'career':
          return this.approveTags({ ids: ids, type: this.interestCategoryApproveGroup }).then(() => {
            this.resetSelectedTags()
            this.interestCategoryApproveGroup = null
          })
        default:
          this.approveTags(ids).then(() => {
            this.resetSelectedTags()
          })
      }
    },

    handleDeleteEntity (id) {
      const confirmAlertOptions = {
        title: 'Delete Tag',
        text: 'Are you sure you want to delete this tag?'
      }
      return alert.confirm(confirmAlertOptions)
        .then(() => {
          switch (this.filters.entity) {
            case 'interest-categories':
              return this.deleteInterestCategory(id).then(() => {
                this.resetSelectedTags()
              })
            case 'personal-interests':
              return this.deletePersonalInterest(id).then(() => {
                this.resetSelectedTags()
              })
            default:
              return this.deleteTag({ id }).then(() => {
                this.resetSelectedTags()
              })
          }
        })
    },

    handleCreateEntity (payload) {
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.createInterestCategory(payload)
        case 'personal-interests':
          return this.createPersonalInterest(payload)
        default:
          this.performingAction = true
          if (this.filters.entity === 'description') {
            payload.type = 'description'
          }
          if (this.filters.entity === 'sponsors') {
            payload.type = 'sponsor'
          }
          return this.storeTag(payload).then(() => {
            this.performingAction = false
          })
      }
    },

    handleRenameEntity (payload) {
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.updateInterestCategory({ id: payload.id, payload }).then(() => {
            this.resetSelectedTags()
          })
        case 'personal-interests':
          return this.updatePersonalInterest({ id: payload.id, payload }).then(() => {
            this.resetSelectedTags()
          })
        default:
          this.performingAction = true
          return this.renameTag({ id: payload.id, payload }).then(() => {
            this.resetSelectedTags()
            this.performingAction = false
          })
      }
    },

    handleMergeEntities (payload) {
      switch (this.filters.entity) {
        case 'interest-categories':
          return this.mergeInterestCategories(payload).then(() => {
            this.resetSelectedTags()
          })
        case 'personal-interests':
          return this.mergePersonalInterests(payload).then(() => {
            this.resetSelectedTags()
          })
        default:
          this.performingAction = true
          if (this.filters.entity === 'description') {
            payload.type = 'description'
          }
          if (this.filters.entity === 'sponsors') {
            payload.type = 'sponsor'
          }
          return this.mergeTags(payload).then(() => {
            this.resetSelectedTags()
            this.performingAction = false
          })
      }
    },

    handleMergeIntoEntities (payload) {
      this.performingAction = true
      return this.mergeIntoTags({ id: payload.id, payload }).then(() => {
        this.resetSelectedTags()
        this.performingAction = false
      })
    },

    resetSearch () {
      this.filters.term = null
      this.handleGetEntities()
    },

    /**
     * Handle searching by name.
     *
     * @return Promise
     */
    async handleGetEntities () {
      /**
       * Following line is necessary due to a race condition when changing tabs as two listeners
       * trigger this method, which triggers the updateTagFiltersQueryStringMethod which in turn
       * retriggers a listener with the old query string values.
       *
       * There are 100% better ways to fix this
       */
      await setTimeout(() => {}, 10)
      this.fetchingTags = true
      this.updateTagFiltersQueryString()

      switch (this.filters.entity) {
        case 'interest-categories':
          return this.getInterestCategories(this.queryParams)
        case 'personal-interests':
          return this.getPersonalInterests(this.queryParams)
        default:
          return this.getTags(this.queryParams)
            .then(() => {
              this.fetchingTags = false
            })
            .then(this.updateFiltersIfPageInvalid)
      }
    },

    /**
     * Reset selected tag type.
     *
     * @return void
     */
    resetTagType () {
      this.filters.type = 'all'
    },

    /**
     * Reset tag selection.
     *
     * @return void
     */
    resetSelectedTags () {
      this.selectedTags = []
    },

    /**
     * Prefill filters upon page refresh.
     *
     * @return void
     */
    prefillFilters () {
      for (let filter in this.filters) {
        if (this.$route.query[filter]) {
          this.filters[filter] = this.$route.query[filter]
        }
      }
    },

    /**
     * Update the filters query string. This either adds or deletes specific filters from
     * the query string which triggers the update in the "$route" watch.
     *
     * @return void
     */
    updateTagFiltersQueryString () {
      let query = assign({}, this.$route.query)

      for (let filter in this.filters) {
        if (this.filters[filter]) {
          query[filter] = this.filters[filter]
        } else {
          delete query[filter]
        }
      }

      this.$router.replace({ query }).catch(error => {
        if (!error || error.name !== 'NavigationDuplicated') {
          throw error
        }
      })
    },

    /**
     * Update the filters if we go past the last page.
     *
     * @return Promise
     */
    updateFiltersIfPageInvalid () {
      if (this.currentEntityPagination.current_page > this.currentEntityPagination.last_page || this.currentEntityPagination.current_page < 1) {
        this.filters.page = 1
      }
      return Promise.resolve()
    }
  }
}
</script>
